/**
 * useDeborahChat - Custom hook for Deborah Chat logic
 * Handles session management, messaging, retry logic, and connection status
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { 
  startChatSession, 
  sendChatMessage, 
  escalateChat, 
  resolveChat 
} from '../lib/api';
import { toast } from 'sonner';

// Configuration
const CONFIG = {
  MESSAGE_TIMEOUT: 30000,      // 30 seconds for message response
  SESSION_TIMEOUT: 15000,      // 15 seconds for session start
  MAX_RETRIES: 3,              // Maximum retry attempts
  RETRY_DELAY: 1000,           // Initial retry delay (exponential backoff)
  CONNECTION_CHECK_INTERVAL: 30000, // Check connection every 30 seconds
};

export const useDeborahChat = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const messagesEndRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const connectionCheckRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (connectionCheckRef.current) clearInterval(connectionCheckRef.current);
    };
  }, []);

  // Connection status check
  useEffect(() => {
    if (isOpen && session) {
      connectionCheckRef.current = setInterval(() => {
        checkConnection();
      }, CONFIG.CONNECTION_CHECK_INTERVAL);
      
      return () => {
        if (connectionCheckRef.current) clearInterval(connectionCheckRef.current);
      };
    }
  }, [isOpen, session]);

  const checkConnection = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch {
      setConnectionStatus('disconnected');
    }
  };

  // Retry with exponential backoff
  const withRetry = useCallback(async (operation, maxRetries = CONFIG.MAX_RETRIES) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const result = await operation();
        setRetryCount(0);
        setLastError(null);
        setConnectionStatus('connected');
        return result;
      } catch (error) {
        lastError = error;
        setRetryCount(attempt + 1);
        
        if (!error.response) {
          setConnectionStatus('reconnecting');
        }
        
        if (error.response?.status >= 400 && error.response?.status < 500) {
          throw error;
        }
        
        if (attempt < maxRetries - 1) {
          const delay = CONFIG.RETRY_DELAY * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    setLastError(lastError);
    throw lastError;
  }, []);

  // Get user-friendly error message
  const getErrorMessage = useCallback((error) => {
    if (!error.response) {
      return 'Network error. Please check your connection.';
    }
    
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    
    switch (status) {
      case 401:
        return 'Session expired. Please refresh the page and try again.';
      case 403:
        return 'Access denied. Please log in again.';
      case 404:
        return 'Chat session not found. Starting a new session...';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
        return 'Server is temporarily unavailable. Please try again in a moment.';
      default:
        return detail || 'Something went wrong. Please try again.';
    }
  }, []);

  // Add system message to chat
  const addSystemMessage = useCallback((content, type = 'info') => {
    const msg = {
      id: `system-${Date.now()}`,
      role: 'system',
      content,
      type,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, msg]);
  }, []);

  // Initialize chat with retry logic
  const initChat = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to chat with Deborah');
      return;
    }
    
    setLoading(true);
    setLastError(null);
    
    try {
      const res = await withRetry(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), CONFIG.SESSION_TIMEOUT);
        
        try {
          const result = await startChatSession();
          clearTimeout(timeoutId);
          return result;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      });
      
      setSession(res.data.session);
      setMessages(res.data.messages || []);
      setConnectionStatus('connected');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      logger.error('Chat session error:', error);
      addSystemMessage(`Unable to connect: ${errorMessage}. Please try again.`, 'error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, withRetry, getErrorMessage, addSystemMessage]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (!session && isAuthenticated) {
      initChat();
    }
  }, [session, isAuthenticated, initChat]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || sending || !session) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setSending(true);
    setIsTyping(true);
    setLastError(null);
    
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await withRetry(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, CONFIG.MESSAGE_TIMEOUT);
        
        try {
          const result = await sendChatMessage(session.id, userMessage);
          clearTimeout(timeoutId);
          return result;
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
          }
          throw error;
        }
      });
      
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        const newMessages = [...filtered];
        
        if (res.data.user_message) {
          newMessages.push({ ...res.data.user_message, status: 'sent' });
        }
        if (res.data.assistant_message) {
          newMessages.push(res.data.assistant_message);
        }
        if (res.data.system_message) {
          newMessages.push(res.data.system_message);
        }
        
        return newMessages;
      });
      
      if (res.data.escalated) {
        setSession(prev => ({ ...prev, status: 'escalated' }));
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      
      setMessages(prev => prev.map(m => 
        m.id === tempUserMsg.id 
          ? { ...m, status: 'failed', error: errorMessage }
          : m
      ));
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
      setIsTyping(false);
    }
  }, [inputValue, sending, session, withRetry, getErrorMessage]);

  const handleRetryMessage = useCallback(async (failedMsg) => {
    setMessages(prev => prev.filter(m => m.id !== failedMsg.id));
    setInputValue(failedMsg.content);
    
    setTimeout(() => {
      handleSend();
    }, 100);
  }, [handleSend]);

  const handleEscalate = useCallback(async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const res = await withRetry(() => escalateChat(session.id));
      setSession(prev => ({ ...prev, status: 'escalated' }));
      
      if (res.data.system_message) {
        setMessages(prev => [...prev, res.data.system_message]);
      }
      
      toast.success('Connected to human support');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [session, withRetry, getErrorMessage]);

  const handleResolve = useCallback(async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const res = await withRetry(() => resolveChat(session.id));
      
      if (res.data.closing_message) {
        setMessages(prev => [...prev, res.data.closing_message]);
      }
      
      setSession(prev => ({ ...prev, status: 'resolved' }));
      toast.success('Chat session closed');
      
      setTimeout(() => {
        setSession(null);
        setMessages([]);
        setLastError(null);
      }, 3000);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [session, withRetry, getErrorMessage]);

  const handleReconnect = useCallback(async () => {
    setConnectionStatus('reconnecting');
    await checkConnection();
    
    if (connectionStatus === 'connected' && !session) {
      initChat();
    }
  }, [connectionStatus, session, initChat]);

  return {
    // State
    isOpen,
    session,
    messages,
    inputValue,
    loading,
    sending,
    isTyping,
    connectionStatus,
    retryCount,
    lastError,
    messagesEndRef,
    
    // Config
    maxRetries: CONFIG.MAX_RETRIES,
    
    // Actions
    setInputValue,
    handleOpen,
    handleClose,
    handleSend,
    handleRetryMessage,
    handleEscalate,
    handleResolve,
    handleReconnect,
    initChat,
  };
};

export default useDeborahChat;
